<template>
  <div ref="megaMenuRef" class="min-h-screen f-col overflow-hidden py-8 c-brand-2" @keydown="handleKeydown">
    <nav :aria-label="$t.primaryNavigationLabel" class="f-col grow">
      <base-tabs-root v-model="activeTabString" class="f-col">
        <div class="f-col">
          <base-tabs-list class="flex gap-1 overflow-x-auto px-6 pb-1 pt-4 scrollbar-sm duration" :class="{ 'op-0 invisible': activeL2?.id }">
            <template v-for="(l1, i) in items.children" :key="`${l1.id}-trigger`">
              <cms-section
                class="contents"
                :section="{
                  name: `${l1.id}-trigger`,
                  items: [{ ...l1, tabIndex: i, renderMode: 'trigger' }],
                }"
              />
            </template>
          </base-tabs-list>
          <div class="relative mt-4" :style="activeL2?.id ? `padding-top: ${pxToRem(inset)}` : undefined">
            <transition mode="out-in" name="slide">
              <div
                v-if="activeChildren"
                :key="`${activeChildren.id}-content`"
                class="slide-left w-full"
                :class="{ 'absolute top-0': activeL2?.id }"
              >
                <cms-section
                  v-if="activeChildren.visualSelectors"
                  :class="{ 'op-0 invisible': activeL2?.id }"
                  :section="{ items: activeChildren.visualSelectors, name: 'visual-selectors' }"
                />
                <cms-section
                  :section="{
                    name: `${activeChildren.id}-content`,
                    items: [{ ...activeChildren, tabIndex: activeTab, renderMode: 'content' }],
                  }"
                />
              </div>
            </transition>
          </div>
        </div>
      </base-tabs-root>

      <div
        class="my-6 f-col grow items-start justify-center"
        :class="activeL2?.id ? 'invisible max-h-0 op-0' : 'duration'"
      >
        <div class="w-full px-6">
          <hr class="mb-6 b-grey-70">
        </div>

        <ul class="f-col gap-3 px-6">
          <li v-for="({ name, to, icon }) in $feature.megaMenuSecondaryNav" :key="name">
            <base-link
              :aria-label="icon ? name : null"
              class="@hactive:c-white c-grey-60 duration"
              :to
              @click="$gtm.push('topNav.onClickTopNavLink', `${name}`)"
            >
              <vf-icon v-if="icon" v-bind="icon" />
              <template v-else>
                {{ name }}
              </template>
            </base-link>
          </li>
        </ul>
        <div class="w-full px-6">
          <hr class="mt-6 b-grey-70">
        </div>
      </div>
    </nav>
    <vf-utility-navigation
      v-if="links"
      class="px-6 body-4"
      :class="activeL2?.id ? 'invisible max-h-0 op-0' : 'duration'"
      :links
      show-loyalty
      @click="emit('close')"
      @sign-in="emit('signIn')"
    />
    <vf-button
      :aria-label="$t.close"
      class="pointer-events-auto fixed bottom-0 right-0 m-6 rounded-xs p-3 duration lg:m-8"
      data-test-id="vf-panel-close"
      style="z-index: 10001"
      variant="primary-inverse"
      @click="emit('close')"
    >
      <vf-icon name="close" size="lg" style="margin: -0.355rem" />
    </vf-button>
  </div>
</template>

<script lang="ts" setup>
import type { MegaMenuContent, MenuItem } from '#types/components/cms/mega-menu'
import type { NavigationLink } from '#types/navigation'
import type { RecursivePartial } from '#types/utils'
import { MegaMenuL1ContextKey } from '#content/contexts/megamenu'

const { items } = defineProps<{
  items: MegaMenuContent
  links?: NavigationLink[]
}>()

const emit = defineEmits<{
  close: []
  signIn: []
}>()

const auth = useAuthStore()
const { $feature, $t, $gtm } = useNuxtApp()

const loyalty = useLoyaltyStore()
const activeL1 = ref<RecursivePartial<MenuItem>>(items.children[0]!)
const activeL2 = ref<MenuItem | undefined>(undefined)
const megaMenuRef = ref<HTMLElement>()
const activeTab = ref(0)
const activeTabString = computed({
  get: () => `${activeTab.value}`,
  set: (val) => {
    activeTab.value = Number.parseInt(val, 10)
    if (items.children[activeTab.value]) {
      activeL1.value = items.children[activeTab.value]!
    }
  }
})

provide(MegaMenuL1ContextKey, { activeL1, activeL2, activeTab })

const activeChildren = computed(() => items.children[activeTab.value])
const inset = computed(() => {
  if (activeL2.value?.children?.length) {
    return (activeL2.value.children.length + 1) * 41
  }
  else {
    return (items.children[activeTab.value]?.columns?.reduce((acc, col) =>
      acc + (col?.children?.length || 0), 0) || 0) * 40
  }
})

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && activeL2.value?.id) {
    activeL2.value = undefined
    e.preventDefault()
  }
}

useSwipe(megaMenuRef, {
  onSwipeEnd(_, dir) {
    if (dir === 'right' && activeL2.value?.id) activeL2.value = undefined
  }
})

onMounted(() => {
  if (auth.loggedIn && !loyalty?.vouchers) loyalty.getVouchers()
})
</script>
